import { useCallback, useState } from 'react'

import {
    makeOptions,
    PLACEHOLDER_OPTIONS,
} from 'features/views/attributes/editable/hooks/useRecordLinksAttributeEditorState'
import { useLookupRecords } from 'features/views/ListView/hooks/useLookupRecords'
import { useStackerUsersObject } from 'features/workspace/stackerUserUtils'

import useDebounce from 'v2/ui/utils/useDebounce'
import useDeepEqualsMemoValue from 'v2/ui/utils/useDeepEqualsMemoValue'

import { SelectOptionItem } from 'ui/components/Select/types'

const DEBOUNCE_RATE = 200

type UseSectionRecordLinksAttributeEditorStateProps = {
    value?: string[] | string
    onChange: (value: string[] | string) => void
    field: FieldDto
    record: RecordDto
}

export function useSectionRecordLinksAttributeEditorState({
    value,
    onChange,
    field,
    record,
}: UseSectionRecordLinksAttributeEditorStateProps) {
    const isSingle = field.type === 'lookup'
    const defaultValue = isSingle ? '' : []
    const localValue = value ?? defaultValue

    const valueRecordSids: string[] = useDeepEqualsMemoValue(valueToArray(localValue, isSingle))

    const [searchQuery, setSearchQuery] = useState('')
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
    const applyDebouncedSearchQuery = useDebounce(setDebouncedSearchQuery, DEBOUNCE_RATE)

    const handleChangeSearchQuery = useCallback(
        (query: string) => {
            setSearchQuery(query)
            applyDebouncedSearchQuery(query)
        },
        [applyDebouncedSearchQuery]
    )

    const { isError, records, isFetching, isLoading, valueRecords } = useLookupRecords({
        field: field,
        searchQuery: debouncedSearchQuery,
        alwaysShowValueRecords: true,
        valueRecordSids: valueRecordSids,
        filterContextRecord: record,
    })

    const handleChange = useCallback(
        (newValue: string[] | string) => {
            onChange(newValue)
        },
        [onChange]
    )

    const usersObject = useStackerUsersObject()
    const allOptions = createOptions(records, usersObject, isLoading)
    const valueOptions = createOptions(valueRecords, usersObject, isLoading)

    return {
        localValue,
        searchQuery,
        handleChangeSearchQuery,
        handleChange,
        isSingle,
        allOptions,
        isError,
        isFetching,
        valueOptions,
    }
}

function valueToArray(localValue: string | string[], isSingle: boolean): string[] {
    if (!localValue) return []

    return isSingle ? [localValue as string] : (localValue as string[])
}

function createOptions(
    records?: RecordDto[],
    usersObject?: ObjectDto,
    isLoading?: boolean
): SelectOptionItem[] {
    if (isLoading) {
        return PLACEHOLDER_OPTIONS
    }

    return makeOptions(records, usersObject).map((option) => ({
        ...option,
        startAvatar: option.avatar,
    }))
}
